import i18next from 'i18next';

import getCurrencySymbol from 'utils/getCurrencySymbol';
import { parseSalaryNumber } from 'utils/salaryValidators';

import { JobSalaryDisplayFields } from 'types/jobs';

import { FALLBACK_VALUE } from 'constants/uiSettings';

const formatSalaryAmount = (
  value: string | number | null | undefined
): string | null => {
  const parsed = parseSalaryNumber(value);

  if (parsed === null) {
    return null;
  }

  return String(parsed);
};

interface CurrencyDisplay {
  prefix: string;
  shouldRepeatPrefix: boolean;
}

const getCurrencyDisplay = (
  currency?: string | null
): CurrencyDisplay | null => {
  if (!currency) {
    return null;
  }

  const symbol = getCurrencySymbol(currency);

  return {
    prefix: symbol,
    shouldRepeatPrefix: symbol !== currency
  };
};

const addCurrencyPrefix = (
  amount: string,
  currencyDisplay: CurrencyDisplay | null
): string => (currencyDisplay ? `${currencyDisplay.prefix}${amount}` : amount);

const formatSalaryRange = (
  salaryFrom: string | number | null | undefined,
  salaryTo: string | number | null | undefined,
  currencyDisplay: CurrencyDisplay | null
): string | null => {
  const from = formatSalaryAmount(salaryFrom);
  const to = formatSalaryAmount(salaryTo);

  if (from && to) {
    const formattedFrom = addCurrencyPrefix(from, currencyDisplay);

    return from === to ? formattedFrom : `${formattedFrom}-${to}`;
  }

  if (from) {
    return i18next.t('job.salary.from', {
      amount: addCurrencyPrefix(from, currencyDisplay)
    });
  }

  if (to) {
    return i18next.t('job.salary.upTo', {
      amount: addCurrencyPrefix(to, currencyDisplay)
    });
  }

  return null;
};

const formatJobSalaryDisplay = (job: JobSalaryDisplayFields): string => {
  if (job.salaryNotSpecified) {
    return FALLBACK_VALUE;
  }

  const currencyDisplay = getCurrencyDisplay(job.currency);
  const range = formatSalaryRange(
    job.salaryFrom,
    job.salaryTo,
    currencyDisplay
  );

  if (range) {
    return range;
  }

  if (job.actualSalary) {
    return job.actualSalary;
  }

  return FALLBACK_VALUE;
};

export default formatJobSalaryDisplay;
